import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Target, Clock, Check, Gift, ChevronLeft, Camera, Sparkles } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import { catalogText } from '@/utils/catalog';

interface Mission {
  id: string;
  progress: number;
  target: number;
  status: string;
  expiresAt: string;
  template: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    type: string;
    criteriaType: string;
    xpReward: number;
  };
}

// Section order on the missions page. SPECIAL missions (one-off events) used to be
// fetched but never rendered; they now get their own section.
const MISSION_SECTIONS = [
  { type: 'DAILY', labelKey: 'missions.daily', Icon: Clock },
  { type: 'WEEKLY', labelKey: 'missions.weekly', Icon: Target },
  { type: 'SPECIAL', labelKey: 'missions.special', Icon: Sparkles },
] as const;

export default function MissionsPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimSuccess, setClaimSuccess] = useState<{ xp: number } | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    loadMissions();
  }, [isAuthenticated, navigate]);

  async function loadMissions() {
    try {
      setLoadError(false);
      const data = await api.get<Mission[]>('/scout/missions');
      setMissions(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  async function claimMission(missionId: string) {
    setClaiming(missionId);
    setClaimError(null);
    try {
      const mission = missions.find((m) => m.id === missionId);
      await api.post('/scout/missions/' + missionId + '/claim', {});
      if (mission) {
        setClaimSuccess({ xp: mission.template.xpReward });
        setTimeout(() => setClaimSuccess(null), 2500);
        window.gtag?.('event', 'mission_claimed', { xp: mission.template.xpReward });
      }
      await loadMissions();
    } catch {
      setClaimError(missionId);
      setTimeout(() => setClaimError(null), 3000);
    } finally {
      setClaiming(null);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gold/5 to-white flex flex-col items-center justify-center px-6">
        <Target className="w-12 h-12 text-steel mb-3" />
        <p className="text-small text-muted-foreground text-center mb-4">{t('missions.loadError')}</p>
        <button
          onClick={() => {
            setLoading(true);
            loadMissions();
          }}
          className="px-5 py-2.5 rounded-xl bg-muted text-small font-medium text-foreground"
        >
          {t('missions.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gold/5 to-white pb-24">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate('/')} className="p-2 -ms-2 rounded-lg hover:bg-warm-faint">
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-section font-bold text-foreground">{t('missions.title')}</h1>
          <p className="text-small text-muted-foreground">{t('missions.subtitle')}</p>
        </div>
      </div>

      {/* Mission sections: one header per mission type, same style and naming pattern */}
      {MISSION_SECTIONS.map(({ type, labelKey, Icon }) => {
        const sectionMissions = missions.filter((m) => m.template.type === type);
        if (sectionMissions.length === 0) return null;
        return (
          <div key={type} className="mx-4 mb-4">
            <h2 className="text-small font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
              <Icon className="w-4 h-4" />
              {t(labelKey)}
            </h2>
            <div className="space-y-3">
              {sectionMissions.map((m) => (
                <MissionCard key={m.id} mission={m} onClaim={claimMission} claiming={claiming === m.id} t={t} />
              ))}
            </div>
          </div>
        );
      })}

      {missions.length === 0 && (
        <div className="mx-4 text-center py-12">
          <Target className="w-12 h-12 text-steel mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">{t('missions.empty')}</p>
          <button
            onClick={() => navigate('/scan')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small active:scale-[0.98] transition-all"
          >
            <Camera className="w-4 h-4" />
            {t('common.openScanner')}
          </button>
        </div>
      )}

      {/* Claim success toast */}
      {claimSuccess && (
        <div className="fixed bottom-20 inset-x-4 z-50 animate-fade-in-up">
          <div className="bg-primary text-white text-small font-bold px-4 py-3 rounded-xl shadow-lg text-center">
            {t('feedback.xpEarned', { xp: claimSuccess.xp })}
          </div>
        </div>
      )}

      {/* Claim error toast */}
      {claimError && (
        <div className="fixed bottom-20 inset-x-4 z-50 animate-fade-in-up">
          <div className="bg-critical text-white text-small font-medium px-4 py-3 rounded-xl shadow-lg text-center">
            {t('missions.claimFailed')}
          </div>
        </div>
      )}
    </div>
  );
}

function ExpiryCountdown({
  expiresAt,
  t,
}: {
  expiresAt: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (remaining <= 0) {
    return (
      <div className="mt-2 flex items-center gap-1 text-caption text-destructive">
        <Clock className="w-3 h-3" />
        <span>{t('missions.expired', { defaultValue: 'Expired' })}</span>
      </div>
    );
  }
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const label =
    hours > 0
      ? t('missions.expiresIn', { defaultValue: '{{hours}}h {{minutes}}m left', hours, minutes })
      : t('missions.expiresInMinutes', { defaultValue: '{{minutes}}m left', minutes });
  return (
    <div className="mt-2 flex items-center gap-1 text-caption text-muted-foreground">
      <Clock className="w-3 h-3" />
      <span>{label}</span>
    </div>
  );
}

function MissionCard({
  mission,
  onClaim,
  claiming,
  t,
}: {
  mission: Mission;
  onClaim: (id: string) => void;
  claiming: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const progressPercent = mission.target > 0 ? Math.min((mission.progress / mission.target) * 100, 100) : 0;
  const isComplete = mission.status === 'COMPLETED';
  const isClaimed = mission.status === 'CLAIMED';

  return (
    <div
      className={`p-4 rounded-xl border shadow-sm ${
        isClaimed
          ? 'bg-warm-faint border-warm-border opacity-60'
          : isComplete
            ? 'bg-primary/5 border-forest/20'
            : 'bg-white border-warm-border'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h3 className="font-semibold text-foreground text-small">
            {catalogText(mission.template.slug, 'name', mission.template.name)}
          </h3>
          <p className="text-caption text-muted-foreground mt-0.5">
            {catalogText(mission.template.slug, 'description', mission.template.description)}
          </p>
        </div>
        <span className="text-caption font-semibold text-forest bg-forest-wash px-2 py-0.5 rounded-full whitespace-nowrap ms-2">
          +{mission.template.xpReward} XP
        </span>
      </div>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex justify-between text-caption text-muted-foreground mb-1">
          <span>
            {mission.progress}/{mission.target}
          </span>
          <span>{Math.round(progressPercent)}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isComplete || isClaimed ? 'bg-primary' : 'bg-gold'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Expiry countdown */}
      {!isClaimed && mission.expiresAt && <ExpiryCountdown expiresAt={mission.expiresAt} t={t} />}

      {/* Claim button */}
      {isComplete && (
        <button
          onClick={() => onClaim(mission.id)}
          disabled={claiming}
          className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary text-white text-small font-semibold hover:bg-forest active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {claiming ? (
            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <>
              <Gift className="w-4 h-4" />
              {t('missions.claim')}
            </>
          )}
        </button>
      )}

      {isClaimed && (
        <div className="mt-3 flex items-center justify-center gap-1.5 text-small text-forest">
          <Check className="w-4 h-4" />
          {t('missions.claimed')}
        </div>
      )}
    </div>
  );
}
